/**
 * Carregador do Google Maps JavaScript API.
 *
 * A chave NÃO está no build: vem de `/api/v1/config`, que a API lê do
 * ambiente. Assim trocar a chave é editar o `.env` e subir de novo — sem
 * reconstruir imagem, sem cache de navegador com chave velha.
 *
 * Sem chave (ambiente sem Maps) ou sem rede, `carregarGoogleMaps()` resolve
 * `null` e quem chamou degrada: o campo de local vira texto livre, o "Como
 * chegar" vira busca pelo nome. Nunca é erro para a pessoa.
 *
 * Uma promessa só, guardada no módulo: o script entra no documento uma vez,
 * por mais telas que peçam.
 */

let carregamento: Promise<typeof google | null> | null = null;

async function chaveDoMaps(): Promise<string | null> {
  try {
    const res = await fetch('/api/v1/config', { credentials: 'same-origin' });
    if (!res.ok) return null;
    const corpo = (await res.json()) as { maps: { key: string } | null };
    return corpo.maps?.key ?? null;
  } catch {
    return null;
  }
}

const NOME_DO_CALLBACK = '__nvGoogleMapsPronto';

function injetarScript(chave: string): Promise<typeof google | null> {
  return new Promise((resolve) => {
    // O `onload` do script dispara antes de `google.maps.importLibrary`
    // existir; o `callback=` do próprio Google só dispara com a API pronta.
    (window as unknown as Record<string, unknown>)[NOME_DO_CALLBACK] = () => {
      delete (window as unknown as Record<string, unknown>)[NOME_DO_CALLBACK];
      resolve(typeof google === 'undefined' ? null : google);
    };
    const params = new URLSearchParams({
      key: chave,
      v: 'weekly',
      loading: 'async',
      language: 'pt-BR',
      callback: NOME_DO_CALLBACK,
    });
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
}

export function carregarGoogleMaps(): Promise<typeof google | null> {
  if (!carregamento) {
    carregamento = (async () => {
      if (typeof google !== 'undefined' && google.maps) return google;
      const chave = await chaveDoMaps();
      if (!chave) return null;
      return injetarScript(chave);
    })();
    // Falhou por rede: a próxima tentativa recomeça do zero.
    void carregamento.then((g) => {
      if (!g) carregamento = null;
    });
  }
  return carregamento;
}

/** Só para testes: esquece a promessa guardada. */
export function esquecerGoogleMaps(): void {
  carregamento = null;
}
